import api from './api';

class ApplicationSummaryService {
  /**
   * Generate and download application summary PDF for an agent
   * @param {number} agentId - The agent ID
   * @returns {Promise<Blob>} PDF blob
   */
  static async downloadApplicationSummaryPDF(agentId) {
    try {
      const response = await api.get(`/application-summary/${agentId}`, {
        responseType: 'blob',
        headers: {
          'Accept': 'application/pdf'
        }
      });

      // Create blob and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      return blob;
    } catch (error) {
      console.error('Error downloading application summary PDF:', error);
      throw error;
    }
  }

  /**
   * Get application summary HTML preview
   * @param {number} agentId - The agent ID
   * @returns {Promise<string>} HTML content
   */
  static async getApplicationSummaryHTML(agentId) {
    try {
      const response = await api.get(`/application-summary/${agentId}?format=html`, {
        headers: {
          'Accept': 'text/html'
        }
      });

      return response.data;
    } catch (error) {
      console.error('Error fetching application summary HTML:', error);
      throw error;
    }
  }

  /**
   * Get application data in JSON format
   * @param {number} agentId - The agent ID
   * @returns {Promise<Object>} Application data
   */
  static async getApplicationData(agentId) {
    try {
      const response = await api.get(`/application-summary/${agentId}/data`);
      return response.data;
    } catch (error) {
      console.error('Error fetching application data:', error);
      throw error;
    }
  }

  /**
   * Helper function to trigger file download
   * @param {Blob} blob - File blob
   * @param {string} filename - Filename for download
   */
  static triggerDownload(blob, filename) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Download application summary PDF with automatic filename
   * @param {number} agentId - The agent ID
   * @param {string} agentName - Agent name for filename
   */
  static async downloadPDF(agentId, agentName = 'agent') {
    try {
      const blob = await this.downloadApplicationSummaryPDF(agentId);
      const filename = `application-summary-${agentName.replace(/\s+/g, '-').toLowerCase()}-${agentId}.pdf`;
      this.triggerDownload(blob, filename);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      throw error;
    }
  }
}

export default ApplicationSummaryService;
